// ===========================================
// IMPORT SPREADSHEET → WHOLESALE ORDERS
// ===========================================
// Upload any .xlsx/.csv, pick the sheet + header row, map its columns to
// order fields, preview, and import. Client-side parse (SheetJS); the mapped
// rows post to /wholesale/orders/import. Works with any layout because the
// human maps the columns.

import { useState } from 'react';
import * as XLSX from 'xlsx';
import { X, UploadCloud, ArrowRight } from 'lucide-react';
import { wholesaleApi } from '../services/api';

type Field = 'customer' | 'product' | 'sku' | 'quantity' | 'price' | 'date' | 'orderKey';
const FIELDS: Array<{ key: Field; label: string; required?: boolean; hint?: string }> = [
  { key: 'customer', label: 'Customer / account', required: true },
  { key: 'product', label: 'Product name', required: true, hint: 'or map SKU instead' },
  { key: 'sku', label: 'SKU (optional)' },
  { key: 'quantity', label: 'Quantity (cases)', required: true },
  { key: 'price', label: 'Unit price (optional)' },
  { key: 'date', label: 'Date (optional)' },
  { key: 'orderKey', label: 'Group into orders by (e.g. PO#)', hint: 'rows sharing this become one order' },
];

const norm = (s: any) => String(s ?? '').trim();
const guess = (headers: string[], patterns: RegExp[]) => {
  const i = headers.findIndex((h) => patterns.some((p) => p.test(h.toLowerCase())));
  return i >= 0 ? String(i) : '';
};

export default function ImportSpreadsheetModal({ onClose, onImported }: { onClose: () => void; onImported: (msg: string) => void }) {
  const [sheets, setSheets] = useState<Record<string, any[][]>>({});
  const [sheetName, setSheetName] = useState('');
  const [headerRow, setHeaderRow] = useState(0);
  const [map, setMap] = useState<Record<Field, string>>({ customer: '', product: '', sku: '', quantity: '', price: '', date: '', orderKey: '' });
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const onFile = async (file: File) => {
    setError('');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const s: Record<string, any[][]> = {};
    for (const name of wb.SheetNames) s[name] = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false }) as any[][];
    setSheets(s);
    const first = wb.SheetNames[0];
    setSheetName(first);
    autoDetect(s[first]);
  };

  const autoDetect = (rows: any[][]) => {
    // header row = the row (first 8) with the most non-empty text cells
    let best = 0, bestScore = -1;
    for (let i = 0; i < Math.min(8, rows.length); i++) {
      const score = (rows[i] || []).filter((c) => typeof c === 'string' && c.trim()).length;
      if (score > bestScore) { bestScore = score; best = i; }
    }
    setHeaderRow(best);
    const headers = (rows[best] || []).map(norm);
    setMap({
      customer: guess(headers, [/customer|distributor|account|name\/company|company/]),
      product: guess(headers, [/product|item|description|sku name/]),
      sku: guess(headers, [/^sku$|item code|upc/]),
      quantity: guess(headers, [/qty|quantity|cases|units/]),
      price: guess(headers, [/price|unit cost|cost/]),
      date: guess(headers, [/date|delivery|ship/]),
      orderKey: guess(headers, [/po ?#|po number|po$|delivery #|order/]),
    });
  };

  const rows = sheets[sheetName] || [];
  const headers = (rows[headerRow] || []).map(norm);
  const dataRows = rows.slice(headerRow + 1).filter((r) => r.some((c) => norm(c)));
  const col = (f: Field) => (map[f] === '' ? -1 : Number(map[f]));

  // Build grouped orders for preview/import
  const buildOrders = () => {
    const groups = new Map<string, { customerName: string; date?: string; notes?: string; items: any[] }>();
    let idx = 0;
    for (const r of dataRows) {
      const customerName = norm(r[col('customer')]);
      const productName = col('product') >= 0 ? norm(r[col('product')]) : '';
      const sku = col('sku') >= 0 ? norm(r[col('sku')]) : '';
      const quantity = parseFloat(norm(r[col('quantity')]).replace(/[^0-9.]/g, '')) || 0;
      if (!customerName || quantity <= 0 || (!productName && !sku)) continue;
      const priceRaw = col('price') >= 0 ? parseFloat(norm(r[col('price')]).replace(/[^0-9.]/g, '')) : NaN;
      const dateRaw = col('date') >= 0 ? r[col('date')] : undefined;
      const key = col('orderKey') >= 0 && norm(r[col('orderKey')]) ? `${customerName}|${norm(r[col('orderKey')])}` : `row-${idx++}`;
      const g = groups.get(key) || { customerName, date: dateRaw ? excelDate(dateRaw) : undefined, notes: col('orderKey') >= 0 ? norm(r[col('orderKey')]) : undefined, items: [] };
      g.items.push({ productName, sku, quantity, ...(isFinite(priceRaw) && priceRaw > 0 ? { unitPriceCents: Math.round(priceRaw * 100) } : {}) });
      groups.set(key, g);
    }
    return Array.from(groups.values());
  };

  const valid = map.customer !== '' && map.quantity !== '' && (map.product !== '' || map.sku !== '');
  const orders = valid ? buildOrders() : [];

  const doImport = async () => {
    setImporting(true); setError('');
    try {
      const res = await wholesaleApi.importOrders(orders as any);
      const d = res.data?.data;
      onImported(`Imported ${d?.created || 0} orders${d?.customersAdded ? ` (+${d.customersAdded} new accounts)` : ''}${d?.skipped ? ` · ${d.skipped} rows skipped` : ''}.`);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || "Couldn't import — check the column mapping.");
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Import from spreadsheet</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {Object.keys(sheets).length === 0 ? (
            <label className="block border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary-300">
              <UploadCloud className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-600 font-medium">Choose a spreadsheet (.xlsx or .csv)</p>
              <p className="text-xs text-slate-400 mt-1">Your PO tracker, sales sheet, deliveries — any layout.</p>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            </label>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Sheet</label>
                  <select value={sheetName} onChange={(e) => { setSheetName(e.target.value); autoDetect(sheets[e.target.value]); }} className="input w-full">
                    {Object.keys(sheets).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Header row</label>
                  <select value={headerRow} onChange={(e) => setHeaderRow(Number(e.target.value))} className="input w-full">
                    {rows.slice(0, 10).map((r, i) => <option key={i} value={i}>Row {i + 1}: {r.slice(0, 4).map(norm).filter(Boolean).join(', ').slice(0, 40)}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Map your columns</p>
                <div className="space-y-2">
                  {FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-3">
                      <div className="w-40 shrink-0 text-sm text-slate-600">{f.label}{f.required && <span className="text-red-400"> *</span>}</div>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      <select value={map[f.key]} onChange={(e) => setMap({ ...map, [f.key]: e.target.value })} className="input flex-1">
                        <option value="">— none —</option>
                        {headers.map((h, i) => h ? <option key={i} value={i}>{h}</option> : null)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {valid && (
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-sm text-slate-700"><span className="font-semibold">{orders.length}</span> orders ready from <span className="font-semibold">{dataRows.length}</span> rows.</p>
                  {orders.slice(0, 3).map((o, i) => (
                    <p key={i} className="text-xs text-slate-500 mt-1 truncate">{o.customerName}: {o.items.map((it: any) => `${it.quantity}× ${it.productName || it.sku}`).join(', ')}</p>
                  ))}
                </div>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex justify-end gap-2">
                <button onClick={() => setSheets({})} className="btn btn-ghost">Choose a different file</button>
                <button onClick={doImport} disabled={!valid || orders.length === 0 || importing} className="btn btn-primary">{importing ? 'Importing…' : `Import ${orders.length} orders`}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Excel serial date or string → YYYY-MM-DD
function excelDate(v: any): string | undefined {
  if (v == null || v === '') return undefined;
  if (typeof v === 'number') {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
}
